import { useMemo } from 'react'
import { Plus } from 'lucide-react'
import PlayCard from './PlayCard'
import type { Formation } from '@/types/Concept.types'
import type { EditorTab } from './Types'
import { ButtonTabs } from '@/components/ui/ButtonTabs'
import { InfiniteScrollSentinel } from '@/components/ui/InfiniteScrollSentinel'
import { LabelFilterBar } from '@/components/labels/LabelFilterBar'
import { usePlaybookLabels, UNTAGGED_FILTER } from '@/hooks/UsePlaybookLabels'

interface FormationsTabProps {
	playbookId: string
	formations: Formation[]
	isLoading: boolean
	activeTab: EditorTab
	onTabChange: (tab: EditorTab) => void
	tabOptions: Array<{ value: EditorTab; label: string }>
	onNewFormation: () => void
	onEditFormation: (formation: Formation) => void
	onDeleteFormation: (id: string, linkedFormationName?: string) => void
	onDuplicateFormation: (id: string) => void
	onUnlinkFormation?: (id: string) => void
	// Pagination props
	onLoadMore?: () => void
	hasMore?: boolean
	isLoadingMore?: boolean
}

export function FormationsTab({
								  playbookId,
								  formations,
								  isLoading,
								  activeTab,
								  onTabChange,
								  tabOptions,
								  onNewFormation,
								  onEditFormation,
								  onDeleteFormation,
								  onDuplicateFormation,
								  onUnlinkFormation,
								  onLoadMore,
								  hasMore,
								  isLoadingMore,
							  }: FormationsTabProps) {
	// Label filtering
	const {
		labels: playbookLabels,
		activeFilter: activeLabelFilter,
		setActiveFilter: setActiveLabelFilter,
		createLabel,
		reorderLabels,
	} = usePlaybookLabels(playbookId)

	// Build ID-to-formation map once (O(n)), then lookup linked names (O(1) each)
	const formationsById = new Map(formations.map(f => [f.id, f]))
	const linkedFormationNames = new Map<string, string>()
	for (const f of formations) {
		if (f.linked_formation_id) {
			const linked = formationsById.get(f.linked_formation_id)
			if (linked) {
				linkedFormationNames.set(f.id, linked.name)
			}
		}
	}

	// Compute label counts
	const { counts: labelCounts, untaggedCount } = useMemo(() => {
		const c: Record<string, number> = {}
		let untagged = 0
		for (const f of formations) {
			if (!f.labels || f.labels.length === 0) {
				untagged++
			} else {
				for (const label of f.labels) {
					c[label.id] = (c[label.id] || 0) + 1
				}
			}
		}
		return { counts: c, untaggedCount: untagged }
	}, [formations])

	// Apply label filter
	const filteredFormations = useMemo(() => {
		if (!activeLabelFilter) return formations
		if (activeLabelFilter === UNTAGGED_FILTER) {
			return formations.filter(f => !f.labels || f.labels.length === 0)
		}
		return formations.filter(f =>
			f.labels?.some(l => l.id === activeLabelFilter),
		)
	}, [formations, activeLabelFilter])

	return (
		<>
			{/* Toolbar */}
			<div className="playbook-editor-tabs">
				<div className="playbook-editor-tabs-content">
					<ButtonTabs
						options={tabOptions}
						value={activeTab}
						onChange={onTabChange}
						aria-label="Content type"
					/>

					<div className="playbook-editor-tabs-divider" />

					<div className="playbook-editor-toolbar-buttons">
						<button
							onClick={onNewFormation}
							className="playbook-editor-action-button playbook-editor-action-button--primary"
						>
							<Plus className="icon-sm" />
							<span>New Formation</span>
						</button>
					</div>

					<div className="playbook-editor-tabs-divider" />

					<LabelFilterBar
						labels={playbookLabels}
						counts={labelCounts}
						totalCount={formations.length}
						untaggedCount={untaggedCount}
						activeFilter={activeLabelFilter}
						onFilterChange={setActiveLabelFilter}
						onCreateLabel={async (data) => { await createLabel(data) }}
						onReorder={reorderLabels}
					/>
				</div>
			</div>

			{/* Content */}
			<div className="playbook-editor-content">
				{isLoading ? (
					<div className="playbook-editor-concepts-loading">
						<p className="playbook-editor-concepts-loading-text">Loading formations...</p>
					</div>
				) : filteredFormations.length === 0 ? (
					<div className="playbook-editor-concepts-empty">
						<p className="playbook-editor-concepts-empty-text">No formations found</p>
						<button
							onClick={onNewFormation}
							className="playbook-editor-empty-button"
						>
							Create your first formation
						</button>
					</div>
				) : (
					<div className="playbook-editor-sections">
						<div className="playbook-editor-section-header">
							<h2>Formations</h2>
							<p className="playbook-editor-section-count">
								{filteredFormations.length} formation
								{filteredFormations.length !== 1 ? 's' : ''}
							</p>
						</div>
						<div className="playbook-editor-grid">
							{filteredFormations.map((formation, index) => (
								<PlayCard
									key={`formation-${formation.id}`}
									id={String(formation.id)}
									name={formation.name}
									animateIndex={index}
									cardType="formation"
									thumbnail={formation.thumbnail ?? undefined}
									onOpen={() => onEditFormation(formation)}
									onRename={() => {/* no-op for formations */
									}}
									onDelete={() => onDeleteFormation(formation.id, linkedFormationNames.get(formation.id))}
									onDuplicate={() => onDuplicateFormation(formation.id)}
									labelObjects={formation.labels?.map(l => ({ id: l.id, name: l.name, color: l.color }))}
									linkedFormationId={formation.linked_formation_id ?? undefined}
									linkedFormationName={linkedFormationNames.get(formation.id)}
									onUnlink={onUnlinkFormation ? () => onUnlinkFormation(formation.id) : undefined}
								/>
							))}
						</div>

						{/* Infinite scroll sentinel */}
						{onLoadMore && (
							<InfiniteScrollSentinel
								onLoadMore={onLoadMore}
								hasMore={hasMore ?? false}
								isLoading={isLoadingMore ?? false}
							/>
						)}
					</div>
				)}
			</div>
		</>
	)
}
